import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { message as antMessage } from 'antd';
import {
  getDashboard,
  getDashboardByUuid,
  listDashboards as apiListDashboards,
  createDashboard as apiCreateDashboard,
  renameDashboard as apiRenameDashboard,
  deleteDashboard as apiDeleteDashboard,
  setDefaultDashboard as apiSetDefault,
  addDashboardItem as apiAddItem,
  updateDashboardLayout as apiUpdateLayout,
  removeDashboardItem as apiRemoveItem,
  updateDashboardItem as apiUpdateItem,
  toggleFavorite as apiToggleFavorite,
  updateDashboard as apiUpdateDashboard,
  getGlobalDashboards as apiListGlobal,
} from '../services/dashboardService';
import { useAuth } from './AuthContext';
import { clearChartData } from '../utils/chartDataCache';
import {
  buildDashboardMeta,
  buildDashboardData,
  dashboardMetaSignature,
} from '../utils/dashboardSerialization';
import {
  listDashboardFilters as apiListFilters,
  createDashboardFilter as apiCreateFilter,
  updateDashboardFilter as apiUpdateFilter,
  deleteDashboardFilter as apiDeleteFilter,
} from '../services/dashboardService';

const DashboardContext = createContext(null);

export function normalizeTabs(tabs) {
  if (!Array.isArray(tabs)) return ['Overview'];
  const seen = new Set();
  const clean = [];
  for (const raw of tabs) {
    const name = String(raw || '').trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    clean.push(name);
  }
  return clean.length > 0 ? clean : ['Overview'];
}

/**
 * Sentinel used as activeDashboardId / tab id while the user is editing
 * a dashboard that has NOT been persisted yet. Strings can never collide
 * with numeric DB ids. Exported so consumers (DashboardPanel tabs, the
 * URL-sync effect, etc.) can branch cleanly on it.
 */
export const DRAFT_DASHBOARD_ID = '__draft__';
const DEFAULT_DRAFT_NAME = 'Untitled';

export function DashboardProvider({ children }) {
  const { isAuthenticated } = useAuth();

  // Multi-dashboard state
  const [dashboards, setDashboards] = useState([]);       // [{id, name, description, tabs, is_default, item_count}]
  const [activeDashboardId, setActiveDashboardId] = useState(null);

  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focusedComponent, setFocusedComponent] = useState(null);
  const loaded = useRef(false);
  // Cache SQL results by item ID so reopening the panel doesn't re-execute queries
  const dataCache = useRef({});
  // Burst-add y-cursor: when the agent fires multiple `dashboard_add` SSE
  // events back-to-back, each addItem() call reads the SAME stale `items`
  // closure value (React state hasn't flushed yet), so they all compute
  // the same bottomEdge and overlap at (x=0, y=N). React-Grid-Layout
  // resolves the collision by compacting, which can squash tiles visually.
  // We track an in-flight per-dashboard "next free y" here so concurrent
  // adds stack correctly. Shape: { [dashboardId]: nextY }.
  const burstY = useRef({});

  // Dashboard-level filter state
  const [dashboardFilters, setDashboardFiltersState] = useState({});
  const [filterVisibility, setFilterVisibilityState] = useState({});
  const [itemColumnData, setItemColumnData] = useState({});

  // Custom dashboard-level filter definitions (persisted server-side under
  // Dashboard.filter_definitions) and the user's current selections for
  // each filter (per-session, never persisted). When the user picks a
  // value for a custom filter, every component's SQL is wrapped with an
  // extra WHERE clause (see utils/dashboardFilterWrapper.js).
  const [customFilterDefs, setCustomFilterDefs] = useState([]);
  const [customFilterValues, setCustomFilterValues] = useState({});

  // Transient "the user is in the middle of editing a custom filter"
  // signal. Set when CustomFilterEditorModal opens, cleared when it
  // closes. Surfaced to the agent via buildDashboardContext() so the
  // assistant can call edit_dashboard_filter / create_dashboard_filter
  // pre-populated with the user's in-flight draft.
  // Shape: { mode: 'create' | 'edit', filter_id: string|null, draft: object|null } | null
  const [filterEditorState, setFilterEditorState] = useState(null);

  // Counter bumped whenever the panel should leave its homepage view
  // (e.g. after add-to-dashboard from chat targets a specific dashboard).
  // DashboardPanel watches this and clears its local showHomepage state.
  const [requestShowDashboard, setRequestShowDashboard] = useState(0);
  const [activeDashboardTab, setActiveDashboardTab] = useState('Overview');

  // Draft dashboard — exists only in memory. Created when the user
  // clicks "+ New Dashboard" from the panel; gets persisted on the first
  // action that needs a server-side row (addItem, rename, etc.). If the
  // user closes the tab/panel/app first, the draft simply disappears
  // and no DB row is ever written. Shape: { id: DRAFT_DASHBOARD_ID, name }.
  const [draftDashboard, setDraftDashboard] = useState(null);

  // Persisted UI prefs: density + view/edit mode (per-user, not per-dashboard).
  const [density, setDensityState] = useState(() => {
    try { return localStorage.getItem('dx.density') || 'default'; } catch { return 'default'; }
  });
  const setDensity = useCallback((d) => {
    setDensityState(d);
    try { localStorage.setItem('dx.density', d); } catch { /* ignore */ }
  }, []);
  // viewMode is always 'view' on initial load — explicit setters in
  // `handleSelectDashboard` / `handleCreateDashboard` control which mode
  // a given dashboard opens in. We deliberately don't persist to
  // localStorage: an old 'edit' value stuck across reloads was letting
  // users drag cards in what they thought was view mode.
  const [viewMode, setViewModeState] = useState('view');
  const setViewMode = useCallback((m) => setViewModeState(m), []);

  // Load dashboards list + default dashboard items after login
  useEffect(() => {
    if (!isAuthenticated || loaded.current) return;
    loaded.current = true;
    setLoading(true);
    // If the URL is targeting a specific dashboard, skip auto-loading the default —
    // DashboardPanel's URL effect will load the requested dashboard. Otherwise the
    // default-load + URL-load can race and overwrite items.
    const urlHasDashboard = typeof window !== 'undefined' &&
      /^\/data\/chat\/dashboard\/[^/]+$/.test(window.location.pathname);
    apiListDashboards()
      .then(async (list) => {
        setDashboards((list || []).map((d) => ({
          ...d,
          description: d.description || null,
          tabs: normalizeTabs(d.tabs),
          tab_settings: d.tab_settings && typeof d.tab_settings === 'object' ? d.tab_settings : {},
        })));
        if (urlHasDashboard) return;
        const defaultDash = list.find(d => d.is_default) || list[0];
        if (defaultDash) {
          setActiveDashboardId(defaultDash.id);
          const full = await getDashboard(defaultDash.id);
          setItems(full.items || []);
          setCustomFilterDefs(Array.isArray(full.filter_definitions) ? full.filter_definitions : []);
          setCustomFilterValues({});
          setFilterVisibilityState(
            full.filter_visibility && typeof full.filter_visibility === 'object'
              ? { ...full.filter_visibility }
              : {}
          );
        }
      })
      .catch((err) => console.error('Failed to load dashboards:', String(err?.message || err).replace(/[\r\n]/g, ' ')))
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  // Switch to a different dashboard (or force-reload the current one)
  const switchDashboard = useCallback(async (dashboardId, { forceReload = false } = {}) => {
    if (dashboardId === activeDashboardId && !forceReload) return;
    setFocusedComponent(null);
    setDashboardFiltersState({});
    setFilterVisibilityState({});
    setItemColumnData({});
    setCustomFilterDefs([]);
    setCustomFilterValues({});
    clearChartData();
    if (forceReload) dataCache.current = {};
    // Switching TO the draft is a local-only operation — there's no
    // server-side row yet, so skip the API call and just reflect the
    // empty draft state.
    if (dashboardId === DRAFT_DASHBOARD_ID) {
      setActiveDashboardId(DRAFT_DASHBOARD_ID);
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const full = await getDashboard(dashboardId);
      setActiveDashboardId(dashboardId);
      setItems(full.items || []);
      setCustomFilterDefs(Array.isArray(full.filter_definitions) ? full.filter_definitions : []);
      setFilterVisibilityState(
        full.filter_visibility && typeof full.filter_visibility === 'object'
          ? { ...full.filter_visibility }
          : {}
      );
      // Update dashboard name in the tab list
      setDashboards(prev => prev.map(d => d.id === dashboardId ? {
        ...d,
        name: full.name,
        description: full.description || null,
        tabs: normalizeTabs(full.tabs),
        tab_settings: full.tab_settings && typeof full.tab_settings === 'object' ? full.tab_settings : {},
        default_tab: full.default_tab || '',
        item_count: (full.items || []).length,
      } : d));
      return full;
    } catch (err) {
      console.error('Failed to switch dashboard:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [activeDashboardId]);

  // Create a new dashboard immediately in the DB. Used by paths where the
  // dashboard is being created together with content (chat "New Dashboard
  // from artifact"), so an orphan empty row isn't a risk.
  const createDashboard = useCallback(async (name) => {
    const created = await apiCreateDashboard(name);
    setDashboards(prev => [...prev, {
      id: created.id,
      uuid: created.uuid,
      name: created.name,
      description: created.description || null,
      tabs: normalizeTabs(created.tabs),
      tab_settings: created.tab_settings && typeof created.tab_settings === 'object' ? created.tab_settings : {},
      is_default: created.is_default,
      item_count: 0,
    }]);
    return created;
  }, []);

  // Global dashboards — cached list that powers the sidebar shortcut
  // submenu. Refreshed on auth, after create/delete, and after any
  // update that may toggle is_global. Kept separate from `dashboards`
  // (which is the user's own working set) because global dashboards
  // come from a different endpoint and should appear in the sidebar
  // even before the user opens the dashboard panel.
  const [globalDashboards, setGlobalDashboards] = useState([]);
  const refreshGlobalDashboards = useCallback(async () => {
    if (!isAuthenticated) return [];
    try {
      const list = await apiListGlobal();
      setGlobalDashboards(Array.isArray(list) ? list : []);
      return list;
    } catch (err) {
      // Silent failure — the menu degrades gracefully to just the two
      // static entries (CREATE / LIST).
      console.warn('Failed to refresh global dashboards:', err);
      return [];
    }
  }, [isAuthenticated]);
  useEffect(() => {
    if (isAuthenticated) refreshGlobalDashboards();
    else setGlobalDashboards([]);
  }, [isAuthenticated, refreshGlobalDashboards]);

  // Persist + open a brand-new dashboard in one step (used by the
  // sidebar's CREATE REPORT). Distinct from createDraftDashboard —
  // this one writes to the DB immediately so the resulting URL is
  // shareable and the dashboard shows up in lists right away.
  const createAndOpenDashboard = useCallback(async (name, { isGlobal = false } = {}) => {
    const created = await apiCreateDashboard(name || DEFAULT_DRAFT_NAME);
    if (isGlobal && created?.id) {
      try {
        await apiUpdateDashboard(created.id, { is_global: true });
        created.is_global = true;
      } catch (err) {
        // Non-fatal — the dashboard exists, just not as global.
        console.warn('Failed to mark dashboard as global:', err);
      }
    }
    setDashboards(prev => [
      ...prev,
      {
        id: created.id,
        uuid: created.uuid,
        name: created.name,
        description: created.description || null,
        tabs: normalizeTabs(created.tabs),
        tab_settings: created.tab_settings && typeof created.tab_settings === 'object' ? created.tab_settings : {},
        is_default: !!created.is_default,
        item_count: 0,
      },
    ]);
    setActiveDashboardId(created.id);
    setItems([]);
    setIsOpen(true);
    setIsFullscreen(false);
    refreshGlobalDashboards();
    return created;
  }, [refreshGlobalDashboards]);

  // Create a *draft* dashboard — local-only, no DB write. The draft is
  // persisted on the first action that needs a real id (addItem, rename,
  // etc.) via commitDraft below. If the user closes the panel/app first,
  // the draft simply disappears.
  //
  // Single-draft policy: clicking + New Dashboard while a draft is
  // already active is a no-op — we just focus the existing draft tab.
  const createDraftDashboard = useCallback((name = DEFAULT_DRAFT_NAME) => {
    const draft = { id: DRAFT_DASHBOARD_ID, name };
    setDraftDashboard((prev) => prev || draft);
    setActiveDashboardId(DRAFT_DASHBOARD_ID);
    setItems([]);
    // Intentionally do NOT call setIsOpen(true) here. Whoever creates
    // a draft is responsible for opening the panel via either a
    // navigate('?mode=dashboard') (which the URL→state effect turns
    // into a single batched setIsOpen+setFullscreen) or by calling
    // openDashboard() themselves. Flipping isOpen here would create
    // an intermediate (isOpen=true, isFullscreen=false) state that
    // App.js's floating-chat auto-expand rule mistakes for a manual
    // hybrid→fullscreen toggle, wrongly showing the chat window when
    // the user just clicked CREATE DASHBOARD from the chat page.
    return draft;
  }, []);

  const discardDraft = useCallback(() => {
    // Always clear the draft state. We intentionally do NOT gate on
    // `activeDashboardId === DRAFT_DASHBOARD_ID` from the closure —
    // that closure value can lag behind reality when a consumer (e.g.
    // App.js menu handler) calls discardDraft just after a state
    // update that flipped activeDashboardId to DRAFT but hasn't yet
    // re-rendered the consumer. We use the functional updater form of
    // setActiveDashboardId so the prev value is read from React's
    // current state, not from a stale closure.
    //
    // We don't touch `items` — when we were on the draft, items is
    // already [] (drafts commit before items get persisted); when we
    // were on a real dashboard, discardDraft must be a no-op for it.
    setDraftDashboard(null);
    setActiveDashboardId((prev) => {
      if (prev !== DRAFT_DASHBOARD_ID) return prev;
      return null;
    });
  }, []);

  // Persist the draft as a real dashboard. `overrideName` lets the
  // rename flow commit with the user-supplied name in a single round
  // trip instead of create-then-rename. Returns the persisted dashboard
  // (with id, uuid). On failure (e.g. 20-cap reached), surfaces an
  // error toast and leaves the draft intact so the user can react.
  const commitDraft = useCallback(async ({ overrideName } = {}) => {
    if (!draftDashboard) return null;
    const name = (overrideName ?? draftDashboard.name) || DEFAULT_DRAFT_NAME;
    let created;
    try {
      created = await apiCreateDashboard(name);
    } catch (err) {
      antMessage.error(err?.message || 'Failed to save dashboard');
      throw err;
    }
    setDashboards(prev => [
      ...prev,
      {
        id: created.id, uuid: created.uuid, name: created.name,
        description: created.description || null,
        tabs: normalizeTabs(created.tabs),
        tab_settings: created.tab_settings && typeof created.tab_settings === 'object' ? created.tab_settings : {},
        is_default: created.is_default, item_count: 0,
      },
    ]);
    setDraftDashboard(null);
    setActiveDashboardId(created.id);
    // Items state stays empty — the caller (addItem) will populate it.
    return created;
  }, [draftDashboard]);

  // Rename a dashboard. If the target is the draft, this is the user's
  // first persistence action — commit the draft to the DB *with* the
  // chosen name instead of doing create-then-rename.
  const renameDashboard = useCallback(async (dashboardId, name) => {
    if (dashboardId === DRAFT_DASHBOARD_ID) {
      return await commitDraft({ overrideName: name });
    }
    const updated = await apiRenameDashboard(dashboardId, name);
    setDashboards(prev => prev.map(d => d.id === dashboardId ? {
      ...d,
      name: updated.name,
      description: updated.description || d.description || null,
      tabs: normalizeTabs(updated.tabs || d.tabs),
      tab_settings: updated.tab_settings && typeof updated.tab_settings === 'object'
        ? updated.tab_settings
        : (d.tab_settings && typeof d.tab_settings === 'object' ? d.tab_settings : {}),
      default_tab: updated.default_tab !== undefined ? updated.default_tab : d.default_tab,
    } : d));
    return updated;
  }, [commitDraft]);

  const updateDashboardMetadata = useCallback(async (dashboardId, metadata) => {
    if (dashboardId === DRAFT_DASHBOARD_ID) {
      throw new Error('Save draft first before updating metadata.');
    }
    const updated = await apiUpdateDashboard(dashboardId, metadata);
    setDashboards((prev) => prev.map((d) => (d.id === dashboardId ? {
      ...d,
      name: updated.name,
      description: updated.description || null,
      tabs: normalizeTabs(updated.tabs),
      tab_settings: updated.tab_settings && typeof updated.tab_settings === 'object'
        ? updated.tab_settings
        : (d.tab_settings && typeof d.tab_settings === 'object' ? d.tab_settings : {}),
      default_tab: updated.default_tab || '',
      item_count: Array.isArray(updated.items) ? updated.items.length : d.item_count,
    } : d)));
    if (dashboardId === activeDashboardId && Array.isArray(updated.items)) {
      setItems(updated.items);
    }
    return updated;
  }, [activeDashboardId]);

  // Delete a dashboard
  const removeDashboard = useCallback(async (dashboardId) => {
    // Closing/deleting the draft means "discard the unsaved changes" —
    // there's no DB row yet, so it's a local-only operation.
    if (dashboardId === DRAFT_DASHBOARD_ID) {
      discardDraft();
      return;
    }
    await apiDeleteDashboard(dashboardId);
    // Keep the sidebar REPORT submenu in sync if a global was removed.
    refreshGlobalDashboards();
    setDashboards(prev => {
      const remaining = prev.filter(d => d.id !== dashboardId);
      // If we deleted the active one, switch to another
      if (dashboardId === activeDashboardId && remaining.length > 0) {
        const next = remaining.find(d => d.is_default) || remaining[0];
        // Promote next as default in list
        const updated = remaining.map(d => d.id === next.id ? { ...d, is_default: true } : d);
        switchDashboard(next.id);
        return updated;
      }
      if (remaining.length === 0) {
        setActiveDashboardId(null);
        setItems([]);
        dataCache.current = {};
        setIsOpen(false);
        setIsFullscreen(false);
      }
      return remaining;
    });
  }, [activeDashboardId, switchDashboard, discardDraft, refreshGlobalDashboards]);

  // Set a dashboard as default (opens first)
  const setDefaultDashboard = useCallback(async (dashboardId) => {
    await apiSetDefault(dashboardId);
    setDashboards(prev => prev.map(d => ({ ...d, is_default: d.id === dashboardId })));
  }, []);

  // Toggle favorite
  const toggleFavorite = useCallback(async (dashboardId) => {
    const res = await apiToggleFavorite(dashboardId);
    setDashboards(prev => prev.map(d => d.id === dashboardId ? { ...d, is_favorite: res.is_favorite } : d));
  }, []);

  // Keep cached data when toggling — cards will use cached data when panel reopens.
  const toggleDashboard = useCallback(() => setIsOpen((prev) => !prev), []);
  const openDashboard = useCallback(() => {
    setIsOpen(true);
  }, []);
  const closeDashboard = useCallback(() => { setIsOpen(false); setIsFullscreen(false); }, []);
  const toggleFullscreen = useCallback(() => setIsFullscreen((prev) => !prev), []);
  // Idempotent fullscreen setter. Prefer this over `toggleFullscreen()`
  // when a caller knows the desired final state — toggle is non-idempotent
  // and gets flipped back when React 18 StrictMode double-invokes effects
  // in dev (the URL→state sync effect was the original casualty).
  const setFullscreen = useCallback((value) => setIsFullscreen(!!value), []);

  const addItem = useCallback(async (item) => {
    // CRITICAL: the agent's `add_to_dashboard` tool emits an item WITHOUT
    // a `dashboard_id` (it doesn't know the active id). If we forward
    // that to the backend as-is, POST /api/dashboard/items falls back to
    // the user's DEFAULT dashboard via `_get_default_dashboard`, which
    // silently mis-routes the new card to the wrong dashboard — even
    // when the user is clearly viewing a different one. Pin the item
    // to the active dashboard up front so every code path below
    // (draft-commit, auto-position, switch logic) sees a target id.
    if (!item.dashboard_id && activeDashboardId) {
      item = { ...item, dashboard_id: activeDashboardId };
    }
    if (!item.tab_name) {
      const activeSummary = dashboards.find((d) => d.id === (item.dashboard_id || activeDashboardId));
      const fallbackTab = activeDashboardTab || normalizeTabs(activeSummary?.tabs)[0] || 'Overview';
      item = { ...item, tab_name: fallbackTab };
    }

    // Step 0: if the caller is adding to the draft (or default-targeting
    // it via active dashboard), commit the draft to the DB first so the
    // item attaches to a real id. This is the single most common
    // commit trigger: + New Dashboard → add a chart.
    let justCommittedDraft = false;
    const targetingDraft =
      item.dashboard_id === DRAFT_DASHBOARD_ID ||
      (!item.dashboard_id && activeDashboardId === DRAFT_DASHBOARD_ID);
    if (targetingDraft) {
      const created = await commitDraft();
      if (!created) throw new Error('Cannot add item: draft commit failed');
      item = { ...item, dashboard_id: created.id };
      justCommittedDraft = true;
    }

    // Auto-position: drop the new item below the lowest existing item.
    // (We used to place it to the right of the rightmost item, which
    // pushed users into horizontal scrolling once the row filled up.)
    // Only auto-position when the caller didn't supply explicit coords.
    //
    // Important: `items` here is the CURRENTLY active dashboard's items.
    // If the caller is adding to a different dashboard (item.dashboard_id
    // ≠ activeDashboardId), `items` is irrelevant — the target dashboard
    // could be empty, in which case computing y from the active board
    // leaves a big whitespace gap above the new card. Drop to y=0 in
    // that case and let the server / next render normalize as needed.
    // Same rule applies right after a draft-commit: the just-created
    // dashboard has no items, so y=0.
    // Clamp tile size to a usable minimum so the agent (or any caller)
    // can't accidentally produce 1×1 / 0×0 tiles. RGL also enforces
    // minW/minH at render time, but persisting sane values keeps the
    // saved layout consistent with what users see. Metric tiles can go
    // as small as 2×1; everything else needs 3×3 to remain readable.
    const isMetric = item.item_type === 'metric';
    const minW = isMetric ? 2 : 3;
    const minH = isMetric ? 1 : 3;
    const defaultW = isMetric ? 3 : 6;
    const defaultH = isMetric ? 2 : 6;
    const safeW = Math.max(minW, Number(item.grid_w) || defaultW);
    const safeH = Math.max(minH, Number(item.grid_h) || defaultH);
    item = { ...item, grid_w: safeW, grid_h: safeH };

    if (item.grid_y === undefined && (item.grid_x === undefined || item.grid_x === 0)) {
      const targetDashId = item.dashboard_id || activeDashboardId;
      const addingToOther = !!(item.dashboard_id && item.dashboard_id !== activeDashboardId);
      const itemsBottom = items.reduce(
        (max, it) => Math.max(max, (it.grid_y || 0) + (it.grid_h || 4)),
        0,
      );
      const baseY = (addingToOther || justCommittedDraft) ? 0 : itemsBottom;
      // If a previous addItem fired in the same React tick already
      // reserved a y-slot below baseY for this dashboard, stack on top
      // of THAT slot instead of overlapping. Otherwise start from
      // baseY. The cursor is cleared on the microtask that follows the
      // apiAddItem await, once `items` will have been updated.
      const reserved = burstY.current[targetDashId];
      const startY = reserved != null && reserved > baseY ? reserved : baseY;
      item = { ...item, grid_x: 0, grid_y: startY };
      burstY.current[targetDashId] = startY + safeH;
    }
    const saved = await apiAddItem(item);
    // Release the burst cursor for this dashboard now that React state
    // is about to be updated with the new item — the next addItem call
    // will read the fresh `items` and compute bottomEdge from there.
    if (item.dashboard_id || activeDashboardId) {
      const tid = item.dashboard_id || activeDashboardId;
      // Only clear if we were the last reservation; nested concurrent
      // bursts (rare) get reset on the next tick anyway.
      delete burstY.current[tid];
    }
    const targetId = item.dashboard_id || activeDashboardId;

    // After a draft commit the freshly-created dashboard is now active
    // (commitDraft already called setActiveDashboardId). Skip the switch
    // branch's re-fetch — we have the saved item right here.
    if (justCommittedDraft) {
      setItems([saved]);
      setDashboards(ds => ds.map(d => d.id === targetId ? { ...d, item_count: 1 } : d));
      setRequestShowDashboard((n) => n + 1);
    } else
    // If the user added the component to a DIFFERENT dashboard than the
    // currently-active one, switch to that dashboard so they see the
    // newly-added card immediately. Otherwise just append it.
    if (item.dashboard_id && item.dashboard_id !== activeDashboardId) {
      // switchDashboard re-fetches items from the API so the new card
      // appears authoritatively without needing the optimistic update.
      try {
        await switchDashboard(item.dashboard_id, { forceReload: true });
      } catch (err) {
        console.error('Failed to switch to target dashboard after add:', err);
      }
      // Bump a counter so DashboardPanel can react and exit its
      // homepage view when present (the panel owns showHomepage as
      // local state; we can't flip it directly from the context).
      setRequestShowDashboard((n) => n + 1);
    } else {
      setItems((prev) => {
        const next = [...prev, saved];
        // Re-derive item_count from the items list rather than incrementing.
        setDashboards(ds => ds.map(d => d.id === targetId ? { ...d, item_count: next.length } : d));
        return next;
      });
    }

    // Always open the dashboard panel after a successful add — the user
    // explicitly asked for this artifact to live there, so make it visible.
    setIsOpen(true);

    return saved;
  }, [activeDashboardId, activeDashboardTab, dashboards, items, switchDashboard, commitDraft]);

  const removeItem = useCallback(async (itemId) => {
    await apiRemoveItem(itemId);
    delete dataCache.current[itemId];
    clearChartData(itemId);
    setItems((prev) => {
      const next = prev.filter((i) => i.id !== itemId);
      // Re-derive item_count from the actual list rather than tracking it
      // incrementally; rapid add/remove was drifting the counter.
      setDashboards(d => d.map(it => it.id === activeDashboardId ? { ...it, item_count: next.length } : it));
      return next;
    });
    setFocusedComponent((prev) => (prev && prev.id === itemId ? null : prev));
    setItemColumnData(prev => { const next = { ...prev }; delete next[itemId]; return next; });
  }, [activeDashboardId]);

  const updateItem = useCallback(async (itemId, updates) => {
    const saved = await apiUpdateItem(itemId, updates);
    delete dataCache.current[itemId];
    clearChartData(itemId);
    setItems((prev) => prev.map((item) => (item.id === itemId ? saved : item)));
    return saved;
  }, []);

  const getCachedData = useCallback((itemId) => dataCache.current[itemId] || null, []);
  const setCachedData = useCallback((itemId, data) => { dataCache.current[itemId] = data; }, []);

  const registerItemColumns = useCallback((itemId, columns, rows, whereFilterColumns) => {
    if (!columns) return;
    const uv = {};
    columns.forEach((col, idx) => {
      const s = new Set();
      if (rows) { for (const row of rows) { if (s.size >= 200) break; s.add(row[idx] != null ? String(row[idx]) : '(null)'); } }
      uv[col] = [...s].sort((a, b) => {
        if (a === '(null)') return 1; if (b === '(null)') return -1;
        const na = Number(a), nb = Number(b);
        if (!isNaN(na) && !isNaN(nb)) return na - nb;
        return a.localeCompare(b);
      });
    });
    // Also include WHERE filter columns (from SQL WHERE clause) that may not be in the result set
    const allCols = [...columns];
    // Map WHERE column → { type, operator, displayLabel } so consumers can
    // choose the right input UI (single-value vs multi-value) AND show the
    // operator in the column label (e.g. "ACCEPTED_AT >=").
    const whereCols = {};
    if (whereFilterColumns) {
      Object.entries(whereFilterColumns).forEach(([col, def]) => {
        whereCols[col] = {
          type: def.type,
          operator: def.operator,
          displayLabel: def.displayLabel || col,
          readonly: !!def.readonly,
          negated: !!def.negated,
          values: [...def.values].map(String),
        };
        if (!allCols.includes(col)) {
          allCols.push(col);
          // Use the parsed filter values as the unique values for this WHERE-only column
          uv[col] = [...def.values].map(String);
        }
      });
    }
    setItemColumnData(prev => {
      const existing = prev[itemId];
      // If the new response has no result-set columns (0-row response from
      // backend often returns columns: []), preserve any wider set of
      // columns we previously registered. Otherwise dashboard-level
      // custom filters that depend on those columns (auto-skip via
      // filterAppliesToCard) would suddenly stop applying and the
      // effectiveSql for the card would flip back to unwrapped, firing
      // a redundant refetch (bug: 2× /execute-sql with the second
      // dropping the custom WHERE clauses).
      const prevCols = existing?.columns || [];
      const mergedCols = [...allCols];
      if (columns.length === 0 && prevCols.length > allCols.length) {
        for (const c of prevCols) {
          if (!mergedCols.includes(c)) mergedCols.push(c);
        }
      }
      // Merge uniqueValues so previously-known distinct values survive
      // an empty response (don't blank out the value pickers).
      const mergedUv = { ...(existing?.uniqueValues || {}), ...uv };
      // Shallow-equal short-circuit: avoid creating a new state object
      // (and the cascading re-renders / effectiveSql recomputes) when
      // nothing actually changed.
      if (existing
          && existing.columns.length === mergedCols.length
          && existing.columns.every((c, i) => c === mergedCols[i])
          && Object.keys(existing.whereColumns || {}).length === Object.keys(whereCols).length
          && Object.keys(whereCols).every(k => {
            const a = existing.whereColumns[k]; const b = whereCols[k];
            return a && b && a.operator === b.operator && a.displayLabel === b.displayLabel
              && a.readonly === b.readonly && a.negated === b.negated
              && a.values.length === b.values.length && a.values.every((v, i) => v === b.values[i]);
          })) {
        return prev;
      }
      return { ...prev, [itemId]: { columns: mergedCols, uniqueValues: mergedUv, whereColumns: whereCols } };
    });
  }, []);

  const setDashboardFilter = useCallback((colName, values) => {
    setDashboardFiltersState(prev => {
      const next = { ...prev };
      if (!values || values.length === 0) delete next[colName];
      else next[colName] = values;
      return next;
    });
  }, []);

  const clearDashboardFilters = useCallback(() => setDashboardFiltersState({}), []);

  const setFilterVisibility = useCallback((colName, visible) => {
    setFilterVisibilityState(prev => {
      const next = { ...prev, [colName]: visible };
      // Fire-and-forget server persist. Drafts (no real id) skip the
      // API — the visibility map will be saved when the draft is
      // committed alongside other dashboard state.
      if (activeDashboardId && activeDashboardId !== DRAFT_DASHBOARD_ID) {
        apiUpdateDashboard(activeDashboardId, { filter_visibility: next })
          .catch((err) => console.warn('Failed to persist filter visibility:', String(err?.message || err).replace(/[\r\n]/g, ' ')));
      }
      return next;
    });
  }, [activeDashboardId]);

  const updateLayout = useCallback(async (layout) => {
    setItems((prev) =>
      prev.map((item) => {
        const entry = layout.find((l) => String(l.i) === String(item.id));
        if (entry) {
          return { ...item, grid_x: entry.x, grid_y: entry.y, grid_w: entry.w, grid_h: entry.h };
        }
        return item;
      })
    );
    try {
      await apiUpdateLayout(layout, activeDashboardId);
    } catch (err) {
      console.error('Failed to save layout:', err);
      // TC-DB-EC-10: notify the user so silent data loss is visible.
      antMessage.error('Failed to save dashboard layout. Please try again.');
    }
  }, [activeDashboardId]);

  // Open a dashboard by UUID (for URL-based access)
  const openDashboardByUuid = useCallback(async (uuid) => {
    // Check if we already have it in the list
    const existing = dashboards.find(d => d.uuid === uuid);
    if (existing) {
      return existing.id;
    }
    // Fetch from server
    const full = await getDashboardByUuid(uuid);
    // Add to dashboards list if not present
    setDashboards(prev => {
      if (prev.find(d => d.id === full.id)) return prev;
      return [...prev, {
        id: full.id,
        uuid: full.uuid,
        name: full.name,
        description: full.description || null,
        tabs: normalizeTabs(full.tabs),
        tab_settings: full.tab_settings && typeof full.tab_settings === 'object' ? full.tab_settings : {},
        is_default: false,
        item_count: (full.items || []).length,
      }];
    });
    return full.id;
  }, [dashboards]);

  const focusComponent = useCallback((item) => {
    setFocusedComponent((prev) => (prev && prev.id === item.id ? null : item));
  }, []);

  const clearFocusedComponent = useCallback(() => setFocusedComponent(null), []);

  // Resolve the active dashboard summary (name/description/flags) from
  // the dashboards list so Meta-JSON carries the right top-level fields.
  const getActiveDashboardSummary = useCallback(() => {
    if (activeDashboardId == null) return null;
    if (draftDashboard && draftDashboard.id === activeDashboardId) return draftDashboard;
    return dashboards.find((d) => d.id === activeDashboardId) || null;
  }, [activeDashboardId, dashboards, draftDashboard]);

  /**
   * Build the Meta-JSON for the currently active dashboard.
   * Pure definition — no row data. Two dashboards with the same Meta-JSON
   * are considered equivalent. Returns null when no dashboard is loaded.
   */
  const buildDashboardMetaJson = useCallback(() => {
    const summary = getActiveDashboardSummary();
    if (!summary && (!items || items.length === 0)) return null;
    return buildDashboardMeta(summary || {}, items, {
      dashboardFilters,
      filterVisibility,
      filterDefinitions: customFilterDefs,
    });
  }, [getActiveDashboardSummary, items, dashboardFilters, filterVisibility, customFilterDefs]);

  /**
   * Stable signature of the active dashboard's Meta-JSON.
   * Useful for cache keys and equality checks across dashboards.
   */
  const getDashboardMetaSignature = useCallback(() => {
    const meta = buildDashboardMetaJson();
    return meta ? dashboardMetaSignature(meta) : '';
  }, [buildDashboardMetaJson]);

  /**
   * Build the Data-JSON payload sent to the data agent's system prompt.
   * Wraps Meta-JSON with per-component cached data (columns + rows). The
   * server slices `sample_rows` to `dashboard.context_row_limit` from
   * agent_config, so we send everything we have cached.
   *
   * Returns null when no dashboard is open (preserves existing behavior).
   */
  const buildDashboardContext = useCallback(() => {
    if (!isOpen) return null;
    const meta = buildDashboardMetaJson();
    const base = meta
      ? buildDashboardData(meta, items, (id) => dataCache.current[id] || null, {
          focusedComponentTitle: focusedComponent ? focusedComponent.title : null,
          activeDashboardId,
          activeTabName: activeDashboardTab,
          dashboardTabs: normalizeTabs(getActiveDashboardSummary()?.tabs),
        })
      : { dashboard_open: true, meta: null, components: [], focused_component: null };
    // Attach the in-flight filter-editor draft (if any) so the agent
    // sees what the user is currently typing in the New/Edit dashboard
    // filter dialog.
    return { ...base, filter_editor: filterEditorState };
  }, [
    isOpen,
    items,
    focusedComponent,
    buildDashboardMetaJson,
    activeDashboardId,
    activeDashboardTab,
    getActiveDashboardSummary,
    filterEditorState,
  ]);

  // ── Chart editor registration ────────────────────────────────────
  // ChartEditorPanel registers itself here when it mounts so the
  // existing Data Chat agent on the same page can read its current
  // state (sql / columns / sample rows / figure) and apply edits to
  // it via setSql / setFigure. Only one editor is active at a time.
  const chartEditorRef = useRef(null);
  const [chartEditorOpen, setChartEditorOpen] = useState(false);
  const registerChartEditor = useCallback((handle) => {
    chartEditorRef.current = handle;
    setChartEditorOpen(!!handle);
  }, []);
  const getChartEditorState = useCallback(() => {
    return chartEditorRef.current?.getState ? chartEditorRef.current.getState() : null;
  }, []);
  const applyChartEditorEdit = useCallback(async (edit) => {
    const h = chartEditorRef.current;
    if (!h) return { applied: false };
    let appliedSql = false;
    let appliedFigure = false;
    let switchedType = null;
    let runResult = null;
    let runError = null;
    if (typeof edit?.sql === 'string' && edit.sql.trim()) {
      try {
        if (h.setSqlAndRun) {
          runResult = await h.setSqlAndRun(edit.sql);
        } else if (h.setSql) {
          h.setSql(edit.sql);
        }
        appliedSql = true;
      } catch (err) {
        runError = err?.detail || err?.message || String(err);
        // The SQL run failed — do NOT mark it applied, do NOT hydrate against
        // null/empty data, and surface the error to the user.
        appliedSql = false;
        antMessage.error(`SQL failed: ${runError}`);
        return {
          applied: false,
          appliedSql: false,
          appliedFigure: false,
          switchedType: null,
          runResult: null,
          runError,
        };
      }
    }
    if (edit?.figure && typeof edit.figure === 'object') {
      if (h.hydrateAndSetFigure) {
        h.hydrateAndSetFigure(edit.figure, {
          columns: runResult?.columns,
          rows: runResult?.rows,
        });
      } else if (h.setFigure) {
        h.setFigure(edit.figure);
      }
      appliedFigure = true;
      const hasTraces = Array.isArray(edit.figure.data) && edit.figure.data.length > 0;
      if (hasTraces && h.setItemType) {
        h.setItemType('chart');
        switchedType = 'chart';
      }
    }
    return {
      applied: appliedSql || appliedFigure,
      appliedSql, appliedFigure, switchedType, runResult, runError,
    };
  }, []);

  // ── Custom dashboard-level filters ───────────────────────────────
  //
  // Definitions are persisted on the server (Dashboard.filter_definitions).
  // The CURRENT VALUES (which options the user has picked) are local UI
  // state — they affect the SQL wrapper applied per card but never get
  // written back to the DB.
  //
  // All four CRUD methods assume an active, persisted dashboard. They
  // throw if the active dashboard is the local DRAFT sentinel.

  const _assertPersistedActive = useCallback(() => {
    if (activeDashboardId == null || activeDashboardId === DRAFT_DASHBOARD_ID) {
      throw new Error('No active dashboard to attach a custom filter to. Save the draft first.');
    }
    return activeDashboardId;
  }, [activeDashboardId]);

  const addCustomFilter = useCallback(async (definition) => {
    const dashId = _assertPersistedActive();
    const created = await apiCreateFilter(dashId, definition);
    setCustomFilterDefs((prev) => [...prev, created]);
    return created;
  }, [_assertPersistedActive]);

  const patchCustomFilter = useCallback(async (filterId, patch) => {
    const dashId = _assertPersistedActive();
    const updated = await apiUpdateFilter(dashId, filterId, patch);
    setCustomFilterDefs((prev) => prev.map((d) => (d.id === filterId ? updated : d)));
    return updated;
  }, [_assertPersistedActive]);

  const removeCustomFilter = useCallback(async (filterId) => {
    const dashId = _assertPersistedActive();
    await apiDeleteFilter(dashId, filterId);
    setCustomFilterDefs((prev) => prev.filter((d) => d.id !== filterId));
    setCustomFilterValues((prev) => {
      if (!(filterId in prev)) return prev;
      const next = { ...prev };
      delete next[filterId];
      return next;
    });
  }, [_assertPersistedActive]);

  /** Set the user's current selection for one custom filter. */
  const setCustomFilterValue = useCallback((filterId, values) => {
    setCustomFilterValues((prev) => {
      const next = { ...prev };
      if (!values || (Array.isArray(values) && values.length === 0)) delete next[filterId];
      else next[filterId] = Array.isArray(values) ? values : [values];
      return next;
    });
  }, []);

  const clearCustomFilterValues = useCallback(() => setCustomFilterValues({}), []);

  /**
   * Refetch the custom-filter definitions for the active dashboard.
   * Useful after the agent creates a filter via SSE — saves a full
   * dashboard reload.
   */
  const refreshCustomFilters = useCallback(async () => {
    if (activeDashboardId == null || activeDashboardId === DRAFT_DASHBOARD_ID) return [];
    try {
      const defs = await apiListFilters(activeDashboardId);
      setCustomFilterDefs(Array.isArray(defs) ? defs : []);
      return defs;
    } catch (err) {
      console.error('Failed to refresh custom dashboard filters:', err);
      return customFilterDefs;
    }
  }, [activeDashboardId, customFilterDefs]);

  // ── Filter-editor focus signal ────────────────────────────────
  //
  // CustomFilterEditorModal registers/clears state here when it opens
  // and closes (and on every form change). buildDashboardContext()
  // forwards this to the agent's Data-JSON as `filter_editor`, so the
  // assistant knows the user is editing a filter and can apply changes
  // via edit_dashboard_filter / create_dashboard_filter.
  const openFilterEditor = useCallback((mode, initial) => {
    setFilterEditorState({
      mode: mode === 'edit' ? 'edit' : 'create',
      filter_id: initial?.id || null,
      draft: initial || null,
    });
  }, []);
  const updateFilterEditorDraft = useCallback((draft) => {
    setFilterEditorState((prev) => (prev ? { ...prev, draft: draft || null } : prev));
  }, []);
  const closeFilterEditor = useCallback(() => setFilterEditorState(null), []);

  return (
    <DashboardContext.Provider
      value={{
        // Multi-dashboard
        dashboards, activeDashboardId, switchDashboard, createDashboard, renameDashboard, removeDashboard, setDefaultDashboard, toggleFavorite, openDashboardByUuid,
        updateDashboardMetadata,
        // Global dashboards (sidebar REPORT submenu)
        globalDashboards, refreshGlobalDashboards, createAndOpenDashboard,
        // Draft (unsaved) dashboard — Grafana-style deferred persistence.
        draftDashboard, createDraftDashboard, commitDraft, discardDraft,
        // Items & panel
        items, isOpen, isFullscreen, loading, toggleDashboard, openDashboard, closeDashboard, toggleFullscreen, setFullscreen, addItem, removeItem, updateItem, updateLayout, getCachedData, setCachedData,
        // Focus & context
        focusedComponent, focusComponent, clearFocusedComponent, buildDashboardContext,
        // Dashboard JSON (Meta-JSON / Data-JSON split)
        buildDashboardMetaJson, getDashboardMetaSignature,
        // Filters
        dashboardFilters, setDashboardFilter, clearDashboardFilters, filterVisibility, setFilterVisibility, itemColumnData, registerItemColumns,
        // Custom dashboard-level filters (user/agent-defined, persisted)
        customFilterDefs, customFilterValues,
        addCustomFilter, patchCustomFilter, removeCustomFilter,
        setCustomFilterValue, clearCustomFilterValues, refreshCustomFilters,
        // Filter-editor focus signal (in-flight draft surfaced to the agent)
        filterEditorState, openFilterEditor, updateFilterEditorDraft, closeFilterEditor,
        // Panel-view nudges
        requestShowDashboard,
        activeDashboardTab, setActiveDashboardTab,
        // UI prefs
        density, setDensity, viewMode, setViewMode,
        // Chart editor bridge (consumed by Data Chat to power chart-edit
        // mode inside the existing agent panel)
        chartEditorOpen, registerChartEditor, getChartEditorState, applyChartEditorEdit,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
}

export function useDashboard() {
  const ctx = useContext(DashboardContext);
  if (!ctx) throw new Error('useDashboard must be used within DashboardProvider');
  return ctx;
}
